/**
 * useGlobalReveal.js — Scroll-reveal hook used across every section.
 *
 * IntersectionObserver-based, reduced-motion aware, with rootMargin support.
 */
import { useEffect, useRef, useState } from "react";

/**
 * useGlobalReveal — Reveal a section when it scrolls into view.
 *
 * Attach the returned ref to the section root, then apply the --in modifier
 * class while `visible` is true.
 *
 * Usage:
 *   const [ref, visible] = useGlobalReveal();
 *   <section ref={ref} className={`sk ${visible ? "sk__head--in" : ""}`}>
 *
 * @param {object}  [opts]
 * @param {number}  [opts.threshold=0.13]   Fraction of element visible before trigger.
 * @param {boolean} [opts.once=true]        Disconnect after the first reveal.
 * @param {string}  [opts.rootMargin="0px"] IntersectionObserver rootMargin, e.g. "0px 0px -60px 0px".
 * @returns {[React.RefObject, boolean]} [ref, visible]
 */
export function useGlobalReveal({ threshold = 0.13, once = true, rootMargin = "0px" } = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (visible && once) return;

    // Skip the animation entirely when the user prefers reduced motion.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }

    const obs = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) obs.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, once, visible, rootMargin]);

  return [ref, visible];
}